// src/hooks/useTabCounts.js
// Returns live pending-item counts per dashboard tab for a given UID.
import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const MAX_RETRIES = 5;

const useTabCounts = (uid) => {
  const [counts, setCounts] = useState({ leaves: 0, tasks: 0, attendance: 0 });
  // Incrementing this forces all listeners to re-subscribe (used on error)
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    // Reset to zero immediately when the target uid changes
    setCounts({ leaves: 0, tasks: 0, attendance: 0 });
    if (!uid) return;

    let retryTimer = null;
    let retryCount = 0;

    const scheduleRetry = () => {
      if (retryCount >= MAX_RETRIES) return;
      retryCount++;
      retryTimer = setTimeout(() => setRetryKey(k => k + 1), 3000 * retryCount);
    };

    // Leaves: pending requests waiting for approval
    const unsubLeaves = onSnapshot(
      query(collection(db, 'leaves'), where('uid', '==', uid)),
      (snap) => {
        const n = snap.docs.filter(d => d.data().status === 'pending').length;
        setCounts(c => ({ ...c, leaves: n }));
      },
      (err) => {
        console.error('[useTabCounts] leaves:', err.code, err.message);
        scheduleRetry();
      }
    );

    // Tasks: active (pending or in-progress)
    const unsubTasks = onSnapshot(
      query(collection(db, 'tasks'), where('uid', '==', uid)),
      (snap) => {
        const n = snap.docs.filter(d => ['pending', 'in-progress'].includes(d.data().status)).length;
        setCounts(c => ({ ...c, tasks: n }));
      },
      (err) => {
        console.error('[useTabCounts] tasks:', err.code, err.message);
        scheduleRetry();
      }
    );

    // Attendance: checked-in records awaiting admin approval
    const unsubAttendance = onSnapshot(
      query(collection(db, 'attendance'), where('uid', '==', uid)),
      (snap) => {
        const n = snap.docs.filter(d => d.data().status === 'Pending').length;
        setCounts(c => ({ ...c, attendance: n }));
      },
      (err) => {
        console.error('[useTabCounts] attendance:', err.code, err.message);
        scheduleRetry();
      }
    );

    return () => {
      clearTimeout(retryTimer);
      unsubLeaves();
      unsubTasks();
      unsubAttendance();
    };
  }, [uid, retryKey]); // retryKey causes full re-subscription when a listener dies

  return counts;
};

export default useTabCounts;

